/**
 * Microsoft Clarity Identify API
 *
 * Ties a Clarity session recording to a logged-in account, so a support ticket
 * can be traced to the sessions behind it. Guests are never identified.
 *
 * Clarity only loads under `@env('production')`, so every call here no-ops
 * everywhere else. No load-polling is needed (unlike Fathom in `fathom.ts`): the
 * Clarity snippet is an inline classic script, which runs during head parsing
 * and installs a queueing stub, whereas `main.ts` is a module script and so
 * runs after parsing. The stub is always there first, and buffers until the
 * real tag arrives.
 *
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/identify-api
 */

import type { User } from '@/types/generated';

type ClarityGlobal = (
  command: 'identify',
  userId: string,
  sessionId?: string,
  pageId?: string,
  userHint?: string
) => void;

declare global {
  interface Window {
    clarity?: ClarityGlobal;
  }
}

export interface ClarityIdentity {
  /** SHA256-hashed by clarity-js on the client before it leaves the browser. */
  userId: string;
  /** NOT hashed — stored and shown verbatim on the Clarity dashboard. */
  userHint: string;
}

/**
 * `userId` is the account id, which Clarity hashes client-side; it is only
 * resolvable against our own database, and filtering the Clarity dashboard by
 * the plain id works because Clarity hashes the search term to match.
 *
 * `userHint` is the one value that reaches Microsoft in plain text, so it
 * carries the username — already public on Codex URLs, and changeable by the
 * user — rather than the account's name or email. The id is appended so a
 * session names the record to look up. Omitting it entirely would make Clarity
 * fall back to a redacted hash of the id, which reads as noise on the
 * dashboard.
 */
export const buildClarityIdentity = (user: Pick<User, 'id' | 'username'>): ClarityIdentity => {
  const userId = String(user.id);

  return {
    userId,
    userHint: user.username ? `${user.username} #${userId}` : `#${userId}`,
  };
};

/**
 * `pageId` is the Inertia page component (`Character/Studio`), not the URL.
 * Entity URLs carry uuids and slugs, so the component name is what groups
 * sessions by screen.
 *
 * Passing `undefined` for the session id is deliberate and safe: clarity-js
 * declares it `sessionId: string = null` and skips it behind an `if (sessionId)`
 * guard, so an explicit `undefined` is indistinguishable from an omitted
 * argument. It cannot be dropped, because `userHint` sits after it.
 */
export const identifyClarityUser = (
  user: Pick<User, 'id' | 'username'> | null | undefined,
  pageId: string
): void => {
  if (typeof window === 'undefined' || !window.clarity || !user) {
    return;
  }

  const { userId, userHint } = buildClarityIdentity(user);

  try {
    window.clarity('identify', userId, undefined, pageId, userHint);
  } catch {
    // Clarity is best-effort session tagging. A failed identify costs an
    // unattributed replay and nothing the user can act on.
  }
};
